<template>
  <div class="prompt-library-page">
    <!-- Hero Section -->
    <div class="hero-section">
      <div class="container">
        <div class="hero-badge">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" />
          </svg>
          Daily Copilot Workflows
        </div>
        <h1>Prompt Library</h1>
        <p class="subtitle">Professional-grade workflows to maximize your daily API usage</p>
      </div>
    </div>

    <!-- Core & Explore Methodology -->
    <section class="methodology-section">
      <div class="container">
        <div class="methodology-card">
          <div class="methodology-header">
            <span class="badge">Recommended Strategy</span>
            <h2>The "Core & Explore" Methodology</h2>
            <p>Maximize returns while staying active in the market every day</p>
          </div>
          
          <div class="split-layout">
            <div class="methodology-column core">
              <div class="icon-badge">🎯</div>
              <h3>Core Portfolio (80%)</h3>
              <div class="metric">+15.92% Alpha vs VOO</div>
              <p class="description">
                Your monthly InvestBuddy Top 5 equal-weight portfolio. Rebalance every 30 days to 
                minimize transaction costs and capture maximum alpha.
              </p>
              <ul class="features">
                <li>✓ 315 trades over 4 years</li>
                <li>✓ $315 in transaction costs (1.98%)</li>
                <li>✓ Set-it-and-forget-it for 30 days</li>
              </ul>
            </div>
            
            <div class="methodology-column explore">
              <div class="icon-badge">🔭</div>
              <h3>Explore Portfolio (20%)</h3>
              <div class="metric">Active Daily Trading</div>
              <p class="description">
                Your active trading capital. Use your 150 daily API calls to monitor macro risk and 
                hunt for standalone momentum plays. To avoid transaction friction, aim to hold Explore 
                swing trades for 7 to 10 days to let the LSTM's edge fully materialize.
              </p>
              <ul class="features">
                <li>✓ Daily discovery with /mcp/discover</li>
                <li>✓ Risk monitoring with /mcp/market-regime</li>
                <li>✓ Validation with /mcp/predict</li>
              </ul>
            </div>
          </div>
          
          <div class="methodology-footer">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
            <p>
              <strong>Why this works:</strong> Weekly rebalancing destroys alpha (-2.55% vs VOO) due to 
              transaction costs. But that doesn't mean you can't trade daily—just separate your core long-term 
              holdings from your active exploration capital.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Daily Workflows -->
    <section class="workflows-section">
      <div class="container">
        <div class="section-header">
          <h2>4 High-Value Daily Workflows</h2>
          <p>Copy these prompts directly into ChatGPT or Claude Desktop</p>
        </div>

        <div class="workflows-grid">
          <!-- Workflow 1: Daily Risk Manager -->
          <div class="workflow-card">
            <div class="workflow-header">
              <div class="workflow-icon">🛡️</div>
              <div>
                <h3>The Daily Risk Manager</h3>
                <span class="workflow-tag">Every Morning Before Market Open</span>
              </div>
            </div>
            
            <p class="workflow-description">
              Even if your core portfolio is locked for 30 days, the macro environment changes daily. 
              Run a morning pulse check to understand if your exposure needs hedging.
            </p>
            
            <div class="prompt-box">
              <div class="prompt-label">
                <span>Copy This Prompt</span>
                <button @click="copyPrompt('risk')" class="copy-btn">
                  <svg v-if="!copied.risk" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
              <code>InvestBuddy, run `getMarketRegime` and give me a summary of today's market health. Then, use `analyzePortfolio` on my current holdings (AAPL, MSFT, NVDA, GOOGL, AMZN) to see if my risk exposure is too high given the current regime.</code>
            </div>
            
            <div class="workflow-value">
              <strong>Value:</strong> If market regime returns "BEAR" or "VOLATILE", you might decide to buy 
              protective puts or tighten stop-losses on your Explore trades, even while the Core portfolio holds steady.
            </div>
          </div>

          <!-- Workflow 2: Swing Trade Sniper -->
          <div class="workflow-card">
            <div class="workflow-header">
              <div class="workflow-icon">🔭</div>
              <div>
                <h3>The Swing Trade Sniper</h3>
                <span class="workflow-tag">Daily Discovery for 5-10 Day Plays</span>
              </div>
            </div>
            
            <p class="workflow-description">
              Market momentum shifts daily. Use /mcp/discover to find new opportunities that trigger 
              your 1% action threshold, then validate with batch predictions.
            </p>
            
            <div class="prompt-box">
              <div class="prompt-label">
                <span>Copy This Prompt</span>
                <button @click="copyPrompt('swing')" class="copy-btn">
                  <svg v-if="!copied.swing" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
              <code>InvestBuddy, use `discoverStocks` to find 3 high-potential stocks with 'high' risk tolerance today. Then, run a `batchPredict` on those 3 tickers and tell me which one has the strongest 10-day forward momentum.</code>
            </div>
            
            <div class="workflow-value">
              <strong>Value:</strong> This fulfills the human urge to "do something" in the market without 
              touching your monthly Core portfolio. Perfect for your 20% Explore capital. Uses the LSTM's 
              10-day prediction horizon for short-term swing trades.
            </div>
          </div>

          <!-- Workflow 3: Earnings Play Validator -->
          <div class="workflow-card">
            <div class="workflow-header">
              <div class="workflow-icon">⚖️</div>
              <div>
                <h3>The Earnings Play Validator</h3>
                <span class="workflow-tag">Remove Emotion from News Events</span>
              </div>
            </div>
            
            <p class="workflow-description">
              When a stock reports earnings or major news breaks, human emotion takes over. Get quantitative 
              grounding to separate "buy the dip" opportunities from falling knives.
            </p>
            
            <div class="prompt-box">
              <div class="prompt-label">
                <span>Copy This Prompt</span>
                <button @click="copyPrompt('earnings')" class="copy-btn">
                  <svg v-if="!copied.earnings" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
              <code>Tesla is down 6% today on factory delays. Run a `predictStock` on TSLA. Does the LSTM see this as a structural breakdown, or is the 10-day forward prediction still showing positive alpha?</code>
            </div>
            
            <div class="workflow-value">
              <strong>Value:</strong> Strips emotion out of reactionary trading. Provides an instant, 
              data-driven second opinion on volatile single-stock events. Perfect for deciding if you should 
              deploy Explore capital on a dip.
            </div>
          </div>

          <!-- Workflow 4: 401(k) Rebalancer -->
          <div class="workflow-card">
            <div class="workflow-header">
              <div class="workflow-icon">🧮</div>
              <div>
                <h3>The 401(k) Rebalancer</h3>
                <span class="workflow-tag">Weekly Portfolio Health Check</span>
              </div>
            </div>
            
            <p class="workflow-description">
              You don't just trade individual stocks—you have 401(k)s, IRAs, and other retirement accounts 
              that rarely get optimized. Use InvestBuddy as your holistic wealth management copilot.
            </p>
            
            <div class="prompt-box">
              <div class="prompt-label">
                <span>Copy This Prompt</span>
                <button @click="copyPrompt('retirement')" class="copy-btn">
                  <svg v-if="!copied.retirement" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
              <code>Here are the core holdings in my tech-heavy IRA: AAPL (30%), MSFT (20%), QQQ (20%), VOO (20%), and Cash (10%). Use `optimizePortfolio` to tell me if my current weightings are optimal for a 'moderate' risk tolerance, considering the current `getMarketRegime`.</code>
            </div>
            
            <div class="workflow-value">
              <strong>Value:</strong> Expands InvestBuddy's utility beyond just picking the Top 5 stocks. 
              Becomes a holistic copilot for your entire investment universe, including tax-advantaged accounts 
              you can't actively day-trade.
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Getting Started -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-card">
          <h2>Ready to Start Using These Workflows?</h2>
          <p>Get your API key and connect InvestBuddy to ChatGPT or Claude Desktop</p>
          <div class="cta-buttons">
            <RouterLink to="/settings/api-keys" class="btn-primary">
              Get Your API Key
            </RouterLink>
            <RouterLink to="/api" class="btn-secondary">
              View Integration Guide
            </RouterLink>
          </div>
          
          <div class="stats-row">
            <div class="stat">
              <div class="stat-value">150</div>
              <div class="stat-label">Free Daily Requests</div>
            </div>
            <div class="stat">
              <div class="stat-value">54.6%</div>
              <div class="stat-label">Model Win Rate</div>
            </div>
            <div class="stat">
              <div class="stat-value">+15.92%</div>
              <div class="stat-label">Alpha vs VOO (Monthly)</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'

interface CopiedState {
  risk: boolean
  swing: boolean
  earnings: boolean
  retirement: boolean
}

const copied = ref<CopiedState>({
  risk: false,
  swing: false,
  earnings: false,
  retirement: false
})

const copyPrompt = async (type: keyof CopiedState) => {
  const prompts = {
    risk: "InvestBuddy, run `getMarketRegime` and give me a summary of today's market health. Then, use `analyzePortfolio` on my current holdings (AAPL, MSFT, NVDA, GOOGL, AMZN) to see if my risk exposure is too high given the current regime.",
    swing: "InvestBuddy, use `discoverStocks` to find 3 high-potential stocks with 'high' risk tolerance today. Then, run a `batchPredict` on those 3 tickers and tell me which one has the strongest 10-day forward momentum.",
    earnings: "Tesla is down 6% today on factory delays. Run a `predictStock` on TSLA. Does the LSTM see this as a structural breakdown, or is the 10-day forward prediction still showing positive alpha?",
    retirement: "Here are the core holdings in my tech-heavy IRA: AAPL (30%), MSFT (20%), QQQ (20%), VOO (20%), and Cash (10%). Use `optimizePortfolio` to tell me if my current weightings are optimal for a 'moderate' risk tolerance, considering the current `getMarketRegime`."
  }
  
  try {
    await navigator.clipboard.writeText(prompts[type])
    copied.value[type] = true
    setTimeout(() => {
      copied.value[type] = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}
</script>

<style scoped>
.prompt-library-page {
  min-height: 100vh;
  background: #f7fafc;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4rem 2rem;
  text-align: center;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.hero-section h1 {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1.25rem;
  opacity: 0.9;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Methodology Section */
.methodology-section {
  padding: 4rem 2rem;
  background: white;
}

.methodology-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.methodology-header {
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  padding: 2rem;
  text-align: center;
  border-bottom: 2px solid #e2e8f0;
}

.badge {
  display: inline-block;
  background: #667eea;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
}

.methodology-header h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.methodology-header p {
  color: #718096;
  font-size: 1.125rem;
}

.split-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
}

.methodology-column {
  padding: 2rem;
}

.methodology-column.core {
  background: linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%);
  border-right: 2px solid #cbd5e0;
}

.methodology-column.explore {
  background: linear-gradient(135deg, #fff5f0 0%, #ffe6f0 100%);
}

.icon-badge {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.methodology-column h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.metric {
  font-size: 1.25rem;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 1rem;
}

.description {
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.features {
  list-style: none;
  padding: 0;
  margin: 0;
}

.features li {
  color: #2d3748;
  padding: 0.5rem 0;
  font-size: 0.9375rem;
}

.methodology-footer {
  background: #fef3c7;
  border-top: 2px solid #fbbf24;
  padding: 1.5rem 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.methodology-footer svg {
  flex-shrink: 0;
  color: #d97706;
  margin-top: 0.125rem;
}

.methodology-footer p {
  color: #78350f;
  line-height: 1.6;
  margin: 0;
}

/* Workflows Section */
.workflows-section {
  padding: 4rem 2rem;
  background: #f7fafc;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-header h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.section-header p {
  font-size: 1.125rem;
  color: #718096;
}

.workflows-grid {
  display: grid;
  gap: 2rem;
}

.workflow-card {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.workflow-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}

.workflow-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.workflow-icon {
  font-size: 2.5rem;
  flex-shrink: 0;
}

.workflow-header h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 0.25rem;
}

.workflow-tag {
  display: inline-block;
  background: #edf2f7;
  color: #4a5568;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.workflow-description {
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.prompt-box {
  background: #2d3748;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.prompt-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #a0aec0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

.copy-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  padding: 0.375rem 0.75rem;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  transition: background 0.2s;
}

.copy-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.prompt-box code {
  display: block;
  color: #f7fafc;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.workflow-value {
  background: #f7fafc;
  border-left: 3px solid #667eea;
  padding: 1rem;
  border-radius: 4px;
  color: #4a5568;
  font-size: 0.9375rem;
  line-height: 1.6;
}

.workflow-value strong {
  color: #2d3748;
}

/* CTA Section */
.cta-section {
  padding: 4rem 2rem;
  background: white;
}

.cta-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  color: white;
}

.cta-card h2 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.cta-card > p {
  font-size: 1.125rem;
  opacity: 0.9;
  margin-bottom: 2rem;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 3rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.875rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  display: inline-block;
}

.btn-primary {
  background: white;
  color: #667eea;
}

.btn-primary:hover {
  background: #f7fafc;
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.5);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: white;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 600px;
  margin: 0 auto;
}

.stat {
  text-align: center;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 0.875rem;
  opacity: 0.9;
}

/* Responsive */
@media (max-width: 768px) {
  .hero-section h1 {
    font-size: 2rem;
  }
  
  .split-layout {
    grid-template-columns: 1fr;
  }
  
  .methodology-column.core {
    border-right: none;
    border-bottom: 2px solid #cbd5e0;
  }
  
  .section-header h2 {
    font-size: 1.75rem;
  }
  
  .workflow-card {
    padding: 1.5rem;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: stretch;
  }
  
  .stats-row {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}
</style>
